"use client";

import { type ModuleAccordionList } from "@lib/types";
import { useInView } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { cleanModuleProps } from "@/components/modules/utils";
import PortableText from "@/components/shared/portable-text";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/ui/cn";
import { getModuleColorTheme } from "@/lib/ui/colorTheme";
import { type ColorTheme } from "@/lib/ui/colorTheme";

type AccordionItemWrapperProps = {
  item: NonNullable<ModuleAccordionList["items"]>[number];
  index: number;
  parallaxScrolling: boolean;
  inViewStates: React.MutableRefObject<Map<string, boolean>>;
  setActiveValue: (value: string | undefined) => void;
  activeValue: string | undefined;
  isBottomPosition: boolean;
  colorTheme?: ColorTheme;
  proseClasses: string;
};

function AccordionItemWrapper({
  item,
  index,
  parallaxScrolling,
  inViewStates,
  setActiveValue,
  activeValue,
  isBottomPosition,
  colorTheme,
  proseClasses,
}: AccordionItemWrapperProps) {
  const isDarkTheme =
    colorTheme === "dark" || colorTheme === "primary" || colorTheme === "secondary";
  const borderColor = isDarkTheme ? "border-white/20" : "border-neutral-light";
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    amount: 0.3,
    margin: "-30% 0px -30% 0px",
  });

  useEffect(() => {
    if (parallaxScrolling) {
      inViewStates.current.set(item._key, inView);

      // Find the first item that's in view
      let firstInViewKey: string | undefined;
      for (const [key, isInView] of inViewStates.current.entries()) {
        if (isInView) {
          firstInViewKey = key;
          break;
        }
      }

      // Update active value if we have an in-view item and it's different from current
      if (firstInViewKey && firstInViewKey !== activeValue) {
        setActiveValue(firstInViewKey);
      }
    }
  }, [inView, item._key, parallaxScrolling, activeValue, setActiveValue, inViewStates]);

  return (
    <div ref={ref}>
      <AccordionItem
        value={item._key}
        className={cn(
          "border-b",
          borderColor,
          index === 0 && "border-t",
          isBottomPosition && index === 0 && "border-t-0",
          parallaxScrolling && "pointer-events-none",
        )}
      >
        <AccordionTrigger
          className={cn(
            "typography-heading-6 text-left uppercase",
            isDarkTheme ? "text-white" : "text-neutral-darkest",
            parallaxScrolling && "[&>svg]:hidden",
          )}
        >
          {item.title}
        </AccordionTrigger>
        <AccordionContent>
          <PortableText
            className={cn("prose prose-sm prose-p:my-4", proseClasses)}
            textClassName="typography-text-medium-normal"
            value={item.body}
          />
        </AccordionContent>
      </AccordionItem>
    </div>
  );
}

export function AccordionListModule({ module }: { module: ModuleAccordionList }) {
  const [eyebrowPosition, parallaxScrolling] = cleanModuleProps(module, [
    "eyebrowPosition",
    "parallaxScrolling",
  ]);
  const eyebrowPositionValue = (eyebrowPosition as "top" | "bottom" | undefined) || "top";
  const isBottomPosition = eyebrowPositionValue === "bottom";
  const parallaxScrollingValue = (parallaxScrolling as boolean | undefined) || false;

  const { themeClasses, isDarkTheme, colorTheme, proseClasses } = getModuleColorTheme(
    module,
    "rounded-lg",
  );

  const [activeValue, setActiveValue] = useState<string | undefined>(module.items?.[0]?._key);
  const containerRef = useRef<HTMLDivElement>(null);
  const inViewStates = useRef<Map<string, boolean>>(new Map());

  if (!module.items || module.items.length === 0) return null;

  const eyebrowBadge = module.eyebrow ? (
    <Badge variant="success" className="w-fit">
      {module.eyebrow}
    </Badge>
  ) : null;

  const headerContent = (
    <div className="space-y-3">
      {module.title ? (
        <h3
          className={cn(
            "typography-heading-3",
            isDarkTheme ? "text-white" : "text-neutral-darkest",
          )}
        >
          {module.title}
        </h3>
      ) : null}
      {module.description ? (
        <PortableText
          className={cn("typography-text-large-normal prose prose-p:my-0", proseClasses)}
          value={module.description}
        />
      ) : null}
    </div>
  );

  const accordionContent = (
    <div className="w-full" ref={containerRef}>
      <Accordion
        type="single"
        collapsible={!parallaxScrollingValue}
        value={activeValue}
        onValueChange={setActiveValue}
        className="w-full"
      >
        {module.items.map((item, index) => (
          <AccordionItemWrapper
            key={item._key}
            item={item}
            index={index}
            parallaxScrolling={parallaxScrollingValue}
            inViewStates={inViewStates}
            setActiveValue={setActiveValue}
            activeValue={activeValue}
            isBottomPosition={isBottomPosition}
            colorTheme={colorTheme}
            proseClasses={proseClasses}
          />
        ))}
      </Accordion>
    </div>
  );

  if (isBottomPosition) {
    return (
      <Container
        as="section"
        className={cn("py-padding-section-lg flex flex-col gap-8", themeClasses)}
      >
        {headerContent}
        <div className="grid gap-8 md:grid-cols-[25%_1fr] md:items-start">
          <div className="pt-padding-element-lg">{eyebrowBadge}</div>
          <div className="w-full">{accordionContent}</div>
        </div>
      </Container>
    );
  }

  return (
    <Container
      as="section"
      className={cn("py-padding-section-lg flex flex-col gap-8", themeClasses)}
    >
      <div className="max-w-3xl space-y-3">
        {eyebrowBadge}
        {headerContent}
      </div>
      <div className="mx-auto w-full max-w-3xl">{accordionContent}</div>
    </Container>
  );
}
